import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Building2, Receipt, FileText, Info, Send } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, Tabs, Card, CardHeader, CardBody, DataTable, Badge, StatusBadge, EmptyState, LoadingBlock } from '../../ui';
import DocumentsTab from '../owner/company/DocumentsTab';
import { useCompanyStats } from '../owner/company/OverviewTab';
import { CompanyCodeCard } from './HomePage';
import TelegramBotTab from './TelegramBotTab';

const PAYMENT_TONE = { completed: 'success', pending: 'warning', refunded: 'neutral' };

/** Моя компания: реквизиты, тариф и оплаты, документы (данные меняет владелец платформы) */
export default function MyCompanyPage() {
  const { t } = useI18n();
  const { company } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'details';
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    api.get(`/admin/tenants/${company.id}`)
      .then((res) => setTenant(res.data.data))
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [company.id, t]);

  return (
    <>
      <PageHeader title={t('myCompany.title')} description={t('myCompany.subtitle')} />
      <Tabs
        className="mb-6"
        value={tab}
        onChange={(v) => setParams(v === 'details' ? {} : { tab: v }, { replace: true })}
        items={[
          { id: 'details', label: t('myCompany.tabs.details'), icon: Building2 },
          { id: 'billing', label: t('myCompany.tabs.billing'), icon: Receipt },
          { id: 'documents', label: t('myCompany.tabs.documents'), icon: FileText },
          { id: 'telegram', label: t('myCompany.tabs.telegram'), icon: Send },
        ]}
      />
      {!tenant ? <LoadingBlock /> : (
        <>
          {tab === 'details' && <Details tenant={tenant} />}
          {tab === 'billing' && <Billing tenant={tenant} />}
          {tab === 'documents' && <DocumentsTab tenant={tenant} />}
          {tab === 'telegram' && <TelegramBotTab tenant={tenant} />}
        </>
      )}
    </>
  );
}

function Hint() {
  const { t } = useI18n();
  return (
    <p className="flex items-center gap-2 text-xs text-zinc-500">
      <Info className="h-3.5 w-3.5 shrink-0" />
      {t('myCompany.changeHint')}
    </p>
  );
}

function Details({ tenant }) {
  const { t } = useI18n();
  const legal = [
    [t('profile.name'), tenant.name],
    [t('profile.legalName'), tenant.legal_name],
    [t('profile.edrpou'), tenant.edrpou],
    [t('profile.city'), tenant.city],
    [t('profile.address'), tenant.address],
    [t('profile.locations'), tenant.locations_count],
  ];
  const contacts = [
    [t('profile.contactName'), tenant.contact_name],
    [t('profile.contactPhone'), tenant.contact_phone],
    [t('profile.contactEmail'), tenant.contact_email],
  ];
  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader title={t('myCompany.details')} actions={<StatusBadge kind="company" value={tenant.status} />} />
          <CardBody><InfoGrid rows={legal} /></CardBody>
          <div className="border-t border-zinc-100 dark:border-zinc-800">
            <CardHeader title={t('myCompany.contacts')} className="border-b-0 pb-0" />
            <CardBody><InfoGrid rows={contacts} /></CardBody>
          </div>
        </Card>
        <CompanyCodeCard code={tenant.company_code} />
      </div>
      <Hint />
    </div>
  );
}

function InfoGrid({ rows }) {
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs text-zinc-500">{label}</dt>
          <dd className="mt-0.5 break-words text-sm text-zinc-900 dark:text-zinc-100">{value || value === 0 ? value : '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

function Billing({ tenant }) {
  const { t, fmt } = useI18n();
  const [stats] = useCompanyStats(tenant.id);
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    api.get(`/admin/tenants/${tenant.id}/payments`)
      .then((res) => setPayments(res.data.data || []))
      .catch((err) => { setPayments([]); toast.error(errorText(err, t('common.error'))); });
  }, [tenant.id, t]);

  const plan = [
    [t('myCompany.plan'), t(`plans.${tenant.plan}`)],
    [t('myCompany.price'), `${fmt.money(tenant.plan_price, tenant.currency)} ${t(`cycles.${tenant.billing_cycle}`).toLowerCase()}`],
    [t('myCompany.nextPayment'), fmt.date(tenant.billing_next_date)],
    [t('myCompany.contract'), tenant.contract_number ? `№ ${tenant.contract_number}${tenant.contract_date ? ` · ${fmt.date(tenant.contract_date)}` : ''}` : null],
  ];

  const columns = [
    { key: 'payment_date', header: t('billing.paymentDate'), sortable: true, render: (p) => <span className="tabular">{fmt.date(p.payment_date)}</span> },
    { key: 'amount', header: t('billing.amount'), sortable: true, align: 'right', render: (p) => <span className="tabular font-medium">{fmt.money(p.amount, p.currency)}</span> },
    {
      key: 'period',
      header: t('billing.period'),
      hideOnMobile: true,
      render: (p) => <span className="tabular text-zinc-500">{p.period_start ? `${fmt.date(p.period_start)} — ${fmt.date(p.period_end)}` : '—'}</span>,
    },
    { key: 'invoice_number', header: t('billing.invoice'), hideOnMobile: true, render: (p) => p.invoice_number || '—' },
    { key: 'status', header: t('billing.paymentStatus'), render: (p) => <Badge tone={PAYMENT_TONE[p.status] ?? 'neutral'}>{t(`billing.paymentStatuses.${p.status}`)}</Badge> },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title={t('billing.planSection')} actions={stats && <StatusBadge kind="billing" value={stats.billing.state} />} />
        <CardBody><InfoGrid rows={plan} /></CardBody>
      </Card>
      <div>
        <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('myCompany.payments')}</h2>
        <DataTable columns={columns} rows={payments || []} loading={!payments} empty={<EmptyState icon={Receipt} title={t('billing.noPayments')} />} />
      </div>
      <Hint />
    </div>
  );
}
